// FINAL PATH: client/src/components/mis/SegmentedToggle.tsx
// STAGING: client/src/MIS_work_2_frontend/mfd/components/SegmentedToggle.tsx
// OWNER: Person 2 (MIS_work_2)
//
// The segmented control from design.md section 7, as one component instead of
// the same markup copied into every card: a bordered `rounded-lg` container with
// `p-1`, each option `rounded-md px-2.5 py-1 text-[11px] font-semibold`, active
// option `bg-primary text-primary-foreground`.
//
// There is deliberately no on/off switch primitive in this codebase; design.md
// says to use this pattern instead, which is why Sample/Live, percent/number and
// map/grid all render through here.

import cn from '@/utils/cn';

export interface SegmentedToggleOption<T extends string> {
  value: T;
  label: string;
  /** Renders the option disabled with an explanatory tooltip. */
  disabledReason?: string;
}

interface MfdToggleProps<T extends string> {
  value: T;
  options: readonly SegmentedToggleOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
  /** Disables every option, e.g. while the change it triggers is in flight. */
  disabled?: boolean;
}

export const SegmentedToggle = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
  disabled = false,
}: MfdToggleProps<T>) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className={cn('inline-flex items-center gap-1 rounded-lg border border-border p-1', className)}
  >
    {options.map((option) => {
      const active = option.value === value;
      const blocked = Boolean(option.disabledReason);
      const button = (
        <button
          type="button"
          aria-pressed={active}
          disabled={disabled || blocked}
          // Folded into the accessible name, because a `title` on a disabled control is
          // announced by almost nothing - the same gap the wrapper below fixes for
          // sighted readers.
          aria-label={
            blocked ? `${option.label} (unavailable). ${option.disabledReason}` : undefined
          }
          onClick={() => onChange(option.value)}
          className={cn(
            'min-h-9 rounded-md px-3 py-1.5 text-[11px] font-semibold leading-4 transition-colors sm:min-h-0 sm:px-2.5 sm:py-1',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            active
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      );

      if (!blocked)
        return (
          <span key={option.value} className="inline-flex">
            {button}
          </span>
        );

      /*
        THE REASON GOES ON A WRAPPER, NOT ON THE BUTTON.

        A `title` on a DISABLED button is never shown: browsers suppress pointer events
        on disabled form controls, so the element never receives the hover that would
        trigger the tooltip. The attribute was there, the sentence was written, and
        hovering the greyed option showed nothing - which is exactly the "dead control
        with no explanation" that disabling it with a reason was meant to avoid.

        The wrapper is not disabled, so it does receive the hover and does show the
        title.
      */
      return (
        <span key={option.value} className="inline-flex" title={option.disabledReason}>
          {button}
        </span>
      );
    })}
  </div>
);
